import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRun } from '@api/processRuns';
import type { ExecutionStep, RunOutcome, RunPhase } from '../editor/dock/ExecutionLog';
import { persistedExecutionSteps } from '../editor/executionOrder';
import type { LastTerminalRun } from '../editor/useProcessRunSession';
import { processRunDetailQueryKey } from './processRunsQueryKeys';

export interface LastRunLogData {
  readonly phase: RunPhase;
  readonly order: ExecutionStep[];
  readonly total: number;
  readonly currentId: null;
  readonly startedAt: number | null;
  readonly finishedAt: number | null;
  readonly runNumber: number | undefined;
  /**
   * Whether the last completed run was a test run. Drives the sidebar
   * title (Test run / Run #N). Undefined when the run dto hasn't been
   * fetched yet.
   */
  readonly isTest: boolean | undefined;
  readonly runOutcome: RunOutcome;
}

/**
 * After a run terminates, fetch its detail and map node states into an
 * {@link ExecutionStep} list so the sidebar's ExecutionLog can show the
 * most recent run's outcome.
 *
 * Returns `null` when no last run exists or while loading.
 */
export function useLastCompletedRunLog(
  processId: string | undefined,
  lastTerminalRun: LastTerminalRun | null,
): LastRunLogData | null {
  const runId = lastTerminalRun?.runId;

  const { data: detail } = useQuery({
    queryKey: processRunDetailQueryKey(processId ?? '', runId ?? ''),
    queryFn: () => getRun(processId!, runId!),
    enabled: !!processId && !!runId,
    staleTime: Infinity,
  });

  return useMemo((): LastRunLogData | null => {
    if (!detail) return null;

    const { run } = detail;
    const order: ExecutionStep[] = persistedExecutionSteps(detail);

    const startMs = run.startedAt
      ? new Date(run.startedAt).getTime()
      : new Date(run.createdAt).getTime();

    let endMs: number | null = null;
    if (run.completedAt) endMs = new Date(run.completedAt).getTime();
    else if (run.cancelledAt) endMs = new Date(run.cancelledAt).getTime();

    const runOutcome: RunOutcome = lastTerminalRun?.outcome ?? 'completed';

    return {
      phase: 'done',
      order,
      total: order.length,
      currentId: null,
      startedAt: startMs,
      finishedAt: endMs,
      runNumber: run.runNumber,
      isTest: run.isTest,
      runOutcome,
    };
  }, [detail, lastTerminalRun?.outcome]);
}
